import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackMixpanel } from '@monorepo/client-common';
import { CheckoutSource, EventTypes } from '@monorepo/types';
import { getEnv } from '@hub/helpers/getters.store';
import { MAX_OTP_RETRIES } from '../constants/quick-purchase.constants';

export type OtpStatus = 'idle' | 'sending' | 'sent' | 'verifying' | 'error';

interface OtpState {
  status: OtpStatus;
  retryCount: number;
  errorMessage: string | null;
  maxRetriesExceeded: boolean;
  sessionId: string | null;
  isExistingUser: boolean | null;
}

/**
 * Hook for managing OTP send/verify for the Quick Purchase flow.
 *
 * sendOtp: sends a 6-digit code to the email, stores the returned sessionId
 *   and isExistingUser flag. No JWT is created.
 *
 * verifyOtp: verifies the code using the stored sessionId. No JWT is created.
 *   Returns isExistingUser so the modal can decide whether to show the country step.
 *
 * Tracks retry count and enforces the 5-attempt limit.
 */
export const useQuickPurchaseOtp = () => {
  const { t } = useTranslation(['quick-purchase']);
  const [state, setState] = useState<OtpState>({
    status: 'idle',
    retryCount: 0,
    errorMessage: null,
    maxRetriesExceeded: false,
    sessionId: null,
    isExistingUser: null,
  });

  const sendOtp = useCallback(
    async (
      email: string,
    ): Promise<{
      sent: boolean;
      sessionId: string | null;
      isExistingUser: boolean | null;
      errorMessage: string | null;
    }> => {
      setState((prev) => ({ ...prev, status: 'sending', errorMessage: null }));
      try {
        const { sessionId, isExistingUser } =
          await getEnv().apiFactory.quickPurchaseService.sendOtp(email);
        setState((prev) => ({
          ...prev,
          status: 'sent',
          sessionId,
          isExistingUser,
        }));
        trackMixpanel({
          event: EventTypes.QUICK_PURCHASE_OTP_SENT,
          properties: {
            checkout_source: CheckoutSource.QuickPurchase,
            is_existing_user: isExistingUser,
          },
        });
        return { sent: true, sessionId, isExistingUser, errorMessage: null };
      } catch (err) {
        const message =
          (err as Error)?.message || t('modal.otp_step.error_send_failed');
        setState((prev) => ({
          ...prev,
          status: 'error',
          errorMessage: message,
        }));
        return {
          sent: false,
          sessionId: null,
          isExistingUser: null,
          errorMessage: message,
        };
      }
    },
    [t],
  );

  const verifyOtp = useCallback(
    async (
      code: string,
    ): Promise<{ verified: boolean; isExistingUser: boolean | null }> => {
      if (state.maxRetriesExceeded || !state.sessionId) {
        return { verified: false, isExistingUser: null };
      }

      setState((prev) => ({
        ...prev,
        status: 'verifying',
        errorMessage: null,
      }));

      try {
        const { isExistingUser } =
          await getEnv().apiFactory.quickPurchaseService.verifyOtp(
            state.sessionId,
            code,
          );
        setState((prev) => ({ ...prev, status: 'idle', isExistingUser }));
        trackMixpanel({
          event: EventTypes.QUICK_PURCHASE_OTP_VERIFIED,
          properties: {
            checkout_source: CheckoutSource.QuickPurchase,
            is_existing_user: isExistingUser,
          },
        });
        return { verified: true, isExistingUser };
      } catch {
        const newRetryCount = state.retryCount + 1;
        const maxRetriesExceeded = newRetryCount >= MAX_OTP_RETRIES;
        const message = maxRetriesExceeded
          ? t('modal.otp_step.error_max_retries')
          : t('modal.otp_step.error_invalid');

        setState((prev) => ({
          ...prev,
          status: 'error',
          retryCount: newRetryCount,
          maxRetriesExceeded,
          errorMessage: message,
        }));

        trackMixpanel({
          event: maxRetriesExceeded
            ? EventTypes.QUICK_PURCHASE_OTP_MAX_RETRIES
            : EventTypes.QUICK_PURCHASE_OTP_FAILED,
          properties: {
            checkout_source: CheckoutSource.QuickPurchase,
            retry_count: newRetryCount,
          },
        });

        return { verified: false, isExistingUser: null };
      }
    },
    [state.retryCount, state.maxRetriesExceeded, state.sessionId, t],
  );

  const clearError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      status: prev.status === 'error' ? 'idle' : prev.status,
      errorMessage: null,
    }));
  }, []);

  return {
    status: state.status,
    retryCount: state.retryCount,
    errorMessage: state.errorMessage,
    maxRetriesExceeded: state.maxRetriesExceeded,
    sessionId: state.sessionId,
    sendOtp,
    verifyOtp,
    clearError,
  };
};
